import {
  readLootCatalogItemAsItemDefinition,
  readShopTemplateAsItemDefinition,
} from './compat';
import { normalizeLegacyInventoryAbility } from './runtime';
import {
  getLegacyItemStackKey,
  stackLegacyInventoryItem,
} from './stacking';
import {
  mapCraftCategoryToLegacyInventoryCategory,
  mapLegacyInventoryCategoryToCraftCategory,
} from './categoryAliases';

const cloneValue = (value) => {
  if (Array.isArray(value)) return value.map(cloneValue);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, innerValue]) => [key, cloneValue(innerValue)]),
    );
  }
  return value;
};

const legacyCategoryFromShopCategory = (category) => {
  if (category === 'ingredient') return 'component';
  if (category === 'food' || category === 'scroll' || category === 'potion') return 'consumable';
  if (category === 'weapon' || category === 'armor') return 'combat';
  return 'misc';
};

const defaultEntryIdFactory = () => `shop-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const defaultAbilityIdFactory = (abilityId) => `${abilityId || 'ability'}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;

const defaultCraftedEntryIdFactory = () => `crafted_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const defaultCraftedAbilityIdFactory = (index) => `ab_${Date.now()}_${index}_${Math.random().toString(36).slice(2, 6)}`;

const defaultPartyPoolEntryIdFactory = () => `party-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const defaultTransferEntryIdFactory = () => `transfer-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const defaultCraftingTableItemIdFactory = () => `ci_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const buildDefaultCraftOutputProvenance = (
  result = {},
  { definitionId = null, acquiredAt = null } = {},
) => ({
  sourceType: 'craft-output',
  definitionId: definitionId || result.definitionId || result.itemId || result.id || null,
  resultId: result.id || null,
  resultName: result.name || null,
  sourceCategory: result.cat || null,
  craftedAt: acquiredAt || null,
});

const cloneLegacyAbility = (ability, abilityIdFactory) => (
  normalizeLegacyInventoryAbility(cloneValue(ability), { abilityIdFactory, preserveId: false })
);

const normalizeLegacyResetType = (value) => {
  const normalized = String(value || 'none').toLowerCase().replace(/[\s_-]+/g, '');
  if (normalized === 'longrest' || normalized === 'long' || normalized === 'lr') return 'longRest';
  if (normalized === 'shortrest' || normalized === 'short' || normalized === 'sr') return 'shortRest';
  return 'none';
};

const materializeCraftResultAbility = (ability = {}, index = 0, abilityIdFactory = defaultCraftedAbilityIdFactory) => {
  const effects = Array.isArray(ability.effects)
    ? ability.effects.filter(Boolean)
    : (typeof ability.effects === 'string' && ability.effects.trim())
      ? ability.effects.split(',').map((value) => value.trim()).filter(Boolean)
      : [];

  let uses = null;
  if (ability.uses && typeof ability.uses === 'object') {
    uses = {
      current: ability.uses.current ?? ability.uses.cur ?? ability.uses.max ?? 1,
      max: ability.uses.max ?? 1,
      resetType: normalizeLegacyResetType(ability.uses.resetType || ability.uses.reset),
    };
  } else if (ability.limited) {
    uses = {
      current: ability.cur ?? ability.max ?? 1,
      max: ability.max ?? 1,
      resetType: normalizeLegacyResetType(ability.reset),
    };
  }

  return {
    id: abilityIdFactory(ability.id || ability.name || index),
    name: ability.name || 'Ability',
    description: ability.description || ability.desc || '',
    effects,
    uses,
  };
};

const materializeLegacyInventoryItem = (
  definition,
  {
    itemId = null,
    sourceCategory = null,
    quantity = 1,
    entryIdFactory = defaultEntryIdFactory,
    abilityIdFactory = defaultAbilityIdFactory,
    acquiredAt = new Date().toISOString(),
    buffsApplyOnUse = definition?.authoredRules?.buffsApplyOnUse === true,
    extraFields = {},
  } = {},
) => ({
  id: entryIdFactory(),
  itemId,
  name: definition.name,
  category: legacyCategoryFromShopCategory(sourceCategory),
  quantity: Math.max(1, quantity || 1),
  equipped: false,
  favorited: false,
  buffs: cloneValue(definition.authoredRules.buffs),
  debuffs: cloneValue(definition.authoredRules.debuffs),
  buffsApplyOnUse,
  notes: definition.authoredText.notes || definition.authoredText.longDesc || definition.authoredText.shortDesc || '',
  uses: definition.authoredRules.usesTemplate ? cloneValue(definition.authoredRules.usesTemplate) : null,
  abilities: cloneValue(definition.authoredRules.abilities)
    .map((ability) => cloneLegacyAbility(ability, abilityIdFactory))
    .filter(Boolean),
  questDetails: '',
  questComplete: false,
  onUseEffects: cloneValue(definition.authoredRules.onUseEffects),
  combatBonuses: cloneValue(definition.authoredRules.combatBonuses),
  basePrice: definition.authoredEconomy.basePrice,
  tradeOnly: definition.authoredEconomy.tradeOnly === true,
  addedDate: acquiredAt,
  _raw: {
    icon: definition.icon || null,
    rar: definition.rarity || null,
    tags: cloneValue(definition.tags),
    cat: sourceCategory,
  },
  ...cloneValue(extraFields),
});

export const materializeLegacyInventoryItemFromShopTemplate = (
  template = {},
  {
    quantity = 1,
    entryIdFactory = defaultEntryIdFactory,
    abilityIdFactory = defaultAbilityIdFactory,
    acquiredAt = new Date().toISOString(),
    extraFields = {},
  } = {},
) => {
  const definition = readShopTemplateAsItemDefinition(template);
  const sourceCategory = definition.sourceCategory || template.category || null;

  return materializeLegacyInventoryItem(definition, {
    itemId: template.id || null,
    sourceCategory,
    quantity,
    entryIdFactory,
    abilityIdFactory,
    acquiredAt,
    extraFields,
  });
};

export const materializeLegacyInventoryItemFromLootCatalogItem = (
  item = {},
  {
    quantity = 1,
    entryIdFactory = defaultEntryIdFactory,
    abilityIdFactory = defaultAbilityIdFactory,
    acquiredAt = new Date().toISOString(),
    lootSource = null,
    extraFields = {},
  } = {},
) => {
  const definition = readLootCatalogItemAsItemDefinition(item);
  const sourceCategory = definition.sourceCategory || item.category || null;
  const hasExplicitBuffsApplyOnUse = typeof item.buffsApplyOnUse === 'boolean';

  return materializeLegacyInventoryItem(definition, {
    itemId: item.id || null,
    sourceCategory,
    quantity,
    entryIdFactory,
    abilityIdFactory,
    acquiredAt,
    buffsApplyOnUse: hasExplicitBuffsApplyOnUse
      ? item.buffsApplyOnUse
      : (item.category === 'potion' || item.category === 'scroll'),
    extraFields: {
      ...(lootSource ? { lootSource } : {}),
      ...extraFields,
    },
  });
};

export const materializeLegacyInventoryItemFromCraftResult = (
  result = {},
  {
    quantity = 1,
    entryIdFactory = defaultCraftedEntryIdFactory,
    abilityIdFactory = defaultCraftedAbilityIdFactory,
    acquiredAt = new Date().toISOString(),
  } = {},
) => {
  if (!result) return null;

  const usesIn = result.uses || null;
  const uses = usesIn
    ? {
        current: (usesIn.cur != null ? usesIn.cur : usesIn.current) ?? usesIn.max ?? 1,
        max: usesIn.max ?? 1,
        resetType: normalizeLegacyResetType(usesIn.reset || usesIn.resetType),
      }
    : { current: 1, max: 1, resetType: 'none' };

  const category = mapCraftCategoryToLegacyInventoryCategory(result.cat) || 'misc';
  const sourceDefinitionId = result.definitionId || result.itemId || result.id || null;
  const hasExplicitBuffsApplyOnUse = typeof result.buffsApplyOnUse === 'boolean';
  const basePrice = result.basePrice ?? result.price ?? null;
  const provenance = result.provenance
    ? cloneValue(result.provenance)
    : buildDefaultCraftOutputProvenance(result, {
      definitionId: sourceDefinitionId,
      acquiredAt,
    });

  return {
    id: entryIdFactory(),
    itemId: sourceDefinitionId,
    name: result.name || 'Crafted Item',
    category,
    quantity: Math.max(1, quantity || 1),
    equipped: false,
    favorited: false,
    buffs: cloneValue(result.buffs || []),
    debuffs: cloneValue(result.debuffs || []),
    buffsApplyOnUse: hasExplicitBuffsApplyOnUse ? result.buffsApplyOnUse : category === 'consumable',
    notes: result.notes || result.desc || '',
    uses,
    abilities: cloneValue(result.abilities || []).map((ability, index) => (
      materializeCraftResultAbility(ability, index, abilityIdFactory)
    )),
    questDetails: result.questDetails || result.dmNotes || '',
    questComplete: false,
    onUseEffects: cloneValue(result.onUse || []),
    combatBonuses: Array.isArray(result.combatBonuses) ? cloneValue(result.combatBonuses) : [],
    basePrice,
    tradeOnly: result.tradeOnly === true,
    quality: result.quality || null,
    provenance,
    addedDate: acquiredAt,
    _raw: {
      icon: result.icon || '📦',
      cat: result.cat || null,
      rar: result.rar || 'Common',
      tags: cloneValue(result.tags || []),
    },
  };
};

export const getCraftResultTableStackKey = (result = {}) => {
  const identityItem = materializeLegacyInventoryItemFromCraftResult(result, {
    quantity: 1,
    entryIdFactory: () => 'craft-table-identity-row',
    abilityIdFactory: (index) => `craft-table-identity-ability-${index}`,
    acquiredAt: '1970-01-01T00:00:00.000Z',
  });
  const stackKey = getLegacyItemStackKey(identityItem);
  if (!stackKey) return null;
  return `${stackKey}:quality:${JSON.stringify(identityItem.quality || null)}`;
};

export const applyCraftResultToCraftingTable = (
  currentTable = {},
  result = {},
  {
    quantity = 1,
    tableItemIdFactory = defaultCraftingTableItemIdFactory,
  } = {},
) => {
  if (!result) return currentTable || {};

  const moveQty = Math.max(1, Math.trunc(Number(quantity)) || 1);
  const nextInv = { ...((currentTable && currentTable.inv) || {}) };
  const nextCustomItems = cloneValue(
    Array.isArray(currentTable?.customItems) ? currentTable.customItems : [],
  );
  const incomingKey = getCraftResultTableStackKey(result);
  const existingItem = incomingKey
    ? nextCustomItems.find((item) => item?._res && getCraftResultTableStackKey(item._res) === incomingKey)
    : null;

  if (existingItem?.id) {
    nextInv[existingItem.id] = (Math.max(0, Number(nextInv[existingItem.id]) || 0)) + moveQty;
    return {
      ...(currentTable || {}),
      inv: nextInv,
      customItems: nextCustomItems,
    };
  }

  let newId = tableItemIdFactory(result);
  if (!newId) newId = defaultCraftingTableItemIdFactory();
  const baseNewId = String(newId);
  let suffix = 2;
  while (nextInv[newId] != null || nextCustomItems.some((item) => item?.id === newId)) {
    newId = `${baseNewId}_${suffix}`;
    suffix += 1;
  }

  nextCustomItems.push({
    id: newId,
    name: result.name || 'Crafted Item',
    icon: result.icon || '📦',
    cat: result.cat || 'tools',
    _res: cloneValue(result),
  });
  nextInv[newId] = moveQty;

  return {
    ...(currentTable || {}),
    inv: nextInv,
    customItems: nextCustomItems,
  };
};

export const applyShopPurchaseToLegacyInventory = (
  inventory = [],
  template = {},
  {
    quantity = 1,
    entryIdFactory = defaultEntryIdFactory,
    abilityIdFactory = defaultAbilityIdFactory,
    acquiredAt = new Date().toISOString(),
    extraFields = {},
  } = {},
) => {
  return stackLegacyInventoryItem(
    inventory,
    materializeLegacyInventoryItemFromShopTemplate(template, {
      quantity,
      entryIdFactory,
      abilityIdFactory,
      acquiredAt,
      extraFields,
    }),
  );
};

export const materializeCraftingTableRowFromLegacyInventoryItem = (
  item = {},
  { quantity = null } = {},
) => {
  if (!item) return null;

  const rowQuantity = Math.max(1, quantity ?? item.quantity ?? 1);
  const snapshot = cloneValue({ ...item, quantity: rowQuantity });
  delete snapshot._raw;

  return {
    item: {
      id: item.id || null,
      name: item.name || 'Item',
      icon: item._raw?.icon || '📦',
      cat: item._raw?.cat || mapLegacyInventoryCategoryToCraftCategory(item.category) || 'materials',
      _invSnapshot: snapshot,
    },
    qty: rowQuantity,
  };
};

export const applyLegacyInventoryTransferToCraftingTable = (
  currentTable = {},
  item = {},
  { quantity = null } = {},
) => {
  const haveQty = Math.max(1, item?.quantity || 1);
  const moveQty = quantity == null
    ? haveQty
    : Math.max(1, Math.min(haveQty, Math.trunc(quantity) || 1));
  if (item?.equipped === true && moveQty >= haveQty) return null;

  const row = materializeCraftingTableRowFromLegacyInventoryItem(item, { quantity: moveQty });
  if (!row?.item?.id) return null;

  const tableInv = { ...((currentTable && currentTable.inv) || {}) };
  const tableCustom = [...((currentTable && currentTable.customItems) || [])];

  tableInv[row.item.id] = (tableInv[row.item.id] || 0) + row.qty;

  const existingIndex = tableCustom.findIndex((candidate) => candidate.id === row.item.id);
  if (existingIndex < 0) {
    tableCustom.push(row.item);
  } else if (row.item._invSnapshot) {
    tableCustom[existingIndex] = {
      ...tableCustom[existingIndex],
      ...row.item,
      _invSnapshot: row.item._invSnapshot,
    };
  }

  return {
    ...(currentTable || {}),
    inv: tableInv,
    customItems: tableCustom,
  };
};

export const materializePartyPoolItemFromLegacyInventoryItem = (
  item = {},
  {
    quantity = 1,
    entryIdFactory = defaultPartyPoolEntryIdFactory,
    abilityIdFactory = defaultAbilityIdFactory,
  } = {},
) => {
  if (!item) return null;

  return {
    ...cloneValue(item),
    id: entryIdFactory(),
    quantity: Math.max(1, quantity || 1),
    equipped: false,
    favorited: false,
    abilities: cloneValue(item.abilities || [])
      .map((ability) => cloneLegacyAbility(ability, abilityIdFactory))
      .filter(Boolean),
  };
};

export const applyLegacyInventoryTransferToPartyPool = (
  poolItems = [],
  item = {},
  {
    quantity = 1,
    entryIdFactory = defaultPartyPoolEntryIdFactory,
    abilityIdFactory = defaultAbilityIdFactory,
  } = {},
) => {
  const pooledItem = materializePartyPoolItemFromLegacyInventoryItem(item, {
    quantity,
    entryIdFactory,
    abilityIdFactory,
  });

  if (!pooledItem) return poolItems;

  return stackLegacyInventoryItem(poolItems || [], pooledItem);
};

export const applyCraftResultTransferToPartyPool = (
  poolItems = [],
  result = {},
  {
    quantity = 1,
    acquiredAt = new Date().toISOString(),
    craftedEntryIdFactory = defaultCraftedEntryIdFactory,
    craftedAbilityIdFactory = defaultCraftedAbilityIdFactory,
    partyEntryIdFactory = defaultPartyPoolEntryIdFactory,
    partyAbilityIdFactory = defaultAbilityIdFactory,
  } = {},
) => {
  const craftedItem = materializeLegacyInventoryItemFromCraftResult(result, {
    quantity,
    entryIdFactory: craftedEntryIdFactory,
    abilityIdFactory: craftedAbilityIdFactory,
    acquiredAt,
  });

  if (!craftedItem) return poolItems;

  return applyLegacyInventoryTransferToPartyPool(poolItems, craftedItem, {
    quantity: craftedItem.quantity || quantity,
    entryIdFactory: partyEntryIdFactory,
    abilityIdFactory: partyAbilityIdFactory,
  });
};

export const materializeTransferredLegacyInventoryItem = (
  item = {},
  {
    quantity = 1,
    sourceName = null,
    entryIdFactory = defaultTransferEntryIdFactory,
    abilityIdFactory = defaultAbilityIdFactory,
    acquiredAt = new Date().toISOString(),
  } = {},
) => {
  const clonedItem = cloneValue(item);
  return {
    ...clonedItem,
    id: entryIdFactory(),
    quantity: Math.max(1, quantity || 1),
    equipped: false,
    favorited: false,
    abilities: cloneValue(item.abilities || [])
      .map((ability) => cloneLegacyAbility(ability, abilityIdFactory))
      .filter(Boolean),
    addedDate: acquiredAt,
    transferredFrom: sourceName || item.transferredFrom || null,
  };
};

export const applyLegacyInventoryTransferToTargetInventory = (
  inventory = [],
  item = {},
  {
    quantity = 1,
    sourceName = null,
    entryIdFactory = defaultTransferEntryIdFactory,
    abilityIdFactory = defaultAbilityIdFactory,
    acquiredAt = new Date().toISOString(),
  } = {},
) => {
  if (!item) return inventory;

  const moveQty = Math.max(1, quantity || 1);
  const transferredItem = materializeTransferredLegacyInventoryItem(item, {
    quantity: moveQty,
    sourceName,
    entryIdFactory,
    abilityIdFactory,
    acquiredAt,
  });

  return stackLegacyInventoryItem(inventory, transferredItem);
};
